import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Tooth, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate(location.state?.from || "/dashboard", { replace: true });
  }, [user, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      navigate(location.state?.from || "/dashboard", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-paper" data-testid="login-page">
      {/* Side panel */}
      <div className="hidden md:flex relative w-1/2 bg-brand-700 text-white p-12 flex-col justify-between overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(20%)',
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white/15 backdrop-blur-md flex items-center justify-center">
            <Tooth size={24} weight="duotone" />
          </div>
          <div>
            <div className="font-heading font-semibold text-lg leading-none">Mediadent</div>
            <div className="text-[10px] uppercase tracking-[0.25em] mt-1 text-white/70">Care Platform</div>
          </div>
        </div>
        <div className="relative z-10 max-w-sm">
          <div className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Studio Sarzana</div>
          <h1 className="text-4xl lg:text-5xl font-heading font-medium leading-tight tracking-tight">
            La cura dei pazienti<br/>incontra la cura del dettaglio.
          </h1>
          <p className="mt-6 text-white/80 leading-relaxed">
            Gestione referti post-operatori, materiali, impianti e accesso paziente sicuro tramite link e PIN.
          </p>
        </div>
        <div className="relative z-10 text-xs text-white/50 tracking-wider">care.mediadentsarzana.it</div>
      </div>

      {/* Login form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md fade-up">
          <div className="md:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-lg bg-brand-700 text-white flex items-center justify-center">
              <Tooth size={20} weight="duotone" />
            </div>
            <div>
              <div className="font-heading font-semibold text-[#1C2321]">Mediadent Care</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-400 mt-0.5">Studio Sarzana</div>
            </div>
          </div>

          <div className="text-xs uppercase tracking-[0.25em] text-brand-400 mb-3">Area riservata</div>
          <h2 className="text-3xl md:text-4xl font-heading font-medium text-[#1C2321] tracking-tight">Accedi</h2>
          <p className="text-[#56635D] mt-2">Studio, Admin o Segreteria.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-brand-400 mb-2">Email</label>
              <input
                type="email"
                autoComplete="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-[#E6E8E6] bg-white focus:border-brand-700 transition-colors"
                placeholder="nome@mediadent.it"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-brand-400 mb-2">Password</label>
              <input
                type="password"
                autoComplete="current-password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-[#E6E8E6] bg-white focus:border-brand-700 transition-colors"
                placeholder="••••••••"
              />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{error}</div>}
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full bg-brand-700 hover:bg-brand-800 disabled:opacity-60 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? "Accesso in corso…" : (<>Accedi <ArrowRight size={16} weight="bold" /></>)}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-[#E6E8E6]">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-2">Sei un paziente?</div>
            <p className="text-sm text-[#56635D]">Apri il link che hai ricevuto via WhatsApp e inserisci il PIN.</p>
            <Link to="/portal" data-testid="patient-portal-link" className="text-sm font-medium text-brand-700 hover:text-brand-800 underline underline-offset-4 decoration-brand-300 inline-flex items-center gap-1 mt-2">
              Hai un codice di accesso? <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
